import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,

  Box,
  Chip
} from '@mui/material';
import VeersaLoaderGif from "../../common/icons/Veersa-loader.gif";
import { toast } from 'react-toastify';
import MessageIcon from '@mui/icons-material/Message';
import CloseIcon from '@mui/icons-material/Close';
import { getEmployeeTasks, getTasksByProjectAllocation } from '../../http/requests/task';
import { toastOptions } from '../../common/utils/toasterOptions';
import AddTaskMessageModal from '../AddTaskMessageModal';
import './ViewTasksModal.css';

const ViewTasksModal = ({ open, onClose, employee }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedTaskForMessage, setSelectedTaskForMessage] = useState(null);

  console.log('ViewTasksModal - Employee received:', employee);
  console.log('ViewTasksModal - ProjectAllocationId:', employee?.projectAllocationId);
  console.log('ViewTasksModal - Employee ID:', employee?.id);

  useEffect(() => {
    if (open && employee) {
      fetchEmployeeTasks();
    }
  }, [open, employee]);

  const fetchEmployeeTasks = async () => {
    // Check if we have projectAllocationId, otherwise fallback to employee ID
    const projectAllocationId = employee?.projectAllocationId;
    const employeeId = employee?.id;
    
    if (!projectAllocationId && !employeeId) {
      console.error('No projectAllocationId or employee ID available for fetching tasks');
      toast.error('Unable to fetch tasks: Required ID not found', toastOptions);
      return;
    }

    setLoading(true);
    try {
      let response;
      
      if (projectAllocationId) {
        console.log('Fetching tasks for projectAllocationId:', projectAllocationId);
        response = await getTasksByProjectAllocation(projectAllocationId);
      } else {
        console.log('Fetching tasks for employee ID:', employeeId);
        response = await getEmployeeTasks(employeeId);
      }
      
      console.log('API Response for employee tasks:', response);
      
      // Handle both cases: response.data or direct array response
      let tasksData = null;
      if (response && response.data && Array.isArray(response.data)) {
        tasksData = response.data;
      } else if (response && Array.isArray(response)) {
        tasksData = response;
      }
      
      if (tasksData) {
        console.log('✅ About to set tasks with data:', tasksData);
        console.log('✅ Data is array?', Array.isArray(tasksData));
        console.log('✅ Data length:', tasksData.length);
        setTasks(tasksData);
        console.log('✅ Tasks state should now be updated');
        
        // Force a re-render check
        setTimeout(() => {
          console.log('✅ Delayed check - current tasks state length:', tasks.length);
        }, 100);
        
        console.log('✅ Successfully loaded tasks from API:', tasksData.length);
      } else {
        console.log('⚠️ API returned no data or invalid format');
        console.log('⚠️ Response structure:', {
          hasResponse: !!response,
          hasData: !!response?.data,
          dataType: typeof response?.data,
          isArray: Array.isArray(response?.data),
          responseType: typeof response,
          isResponseArray: Array.isArray(response)
        });
        setTasks([]);
        toast.info('No previous tasks found for this employee', toastOptions);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      
      // Only show error for non-404 errors
      if (error?.response?.status === 404) {
        console.log('No tasks found for employee (404)');
        setTasks([]);
        toast.info('No previous tasks found for this employee', toastOptions);
      } else {
        toast.error('Failed to fetch tasks', toastOptions);
        setTasks([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (task) => {
    setSelectedTaskForMessage(task);
    setMessageModalOpen(true);
  };

  const handleCloseMessageModal = () => {
    setMessageModalOpen(false);
    setSelectedTaskForMessage(null);
  };

  const handleMessageAdded = (taskId) => {
    // Message success toast is already shown in AddTaskMessageModal
    // Could refresh the task list here if needed
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="view-tasks-modal"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Previous Tasks - {employee?.resourceName || 'Employee'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <div style={{
              backgroundColor: 'white',
              padding: '15px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={VeersaLoaderGif} 
                alt="Loading..." 
                style={{ width: '52px', height: '52px', marginBottom: '10px' }}
              />
              <span style={{ color: '#1976d2', fontSize: '14px', fontWeight: '500' }}>Loading...</span>
            </div>
          </Box>
        ) : tasks.length > 0 ? (
          <Table className="tasks-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Assigned Date</strong></TableCell>
                <TableCell><strong>Assigned By</strong></TableCell>
                <TableCell><strong>Assigned To</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.taskId} hover>
                  <TableCell>{formatDate(task.assignedDate)}</TableCell>
                  <TableCell>{task.assignedBy}</TableCell>
                  <TableCell>{task.assignedTo}</TableCell>
                  <TableCell>
                    <Typography variant="body2" className="task-title">
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography variant="caption" color="textSecondary" className="task-description">
                        {task.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.status}
                      color={getStatusColor(task.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleSendMessage(task)}
                      color="primary"
                      size="small"
                      title="Send Message"
                    >
                      <MessageIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="textSecondary">
              No previous tasks found
            </Typography>
            <Typography variant="body2" color="textSecondary" mt={1}>
              This employee has no previously assigned tasks.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      {/* Add Task Message Modal */}
      <AddTaskMessageModal
        open={messageModalOpen}
        onClose={handleCloseMessageModal}
        task={selectedTaskForMessage}
        onMessageAdded={handleMessageAdded}
      />
    </Dialog>
  );
};

export default ViewTasksModal;
